import React, { useEffect, useLayoutEffect } from 'react';
import { useSelector } from 'react-redux';
import { createTheme, Dialog, Skeleton, ThemeProvider } from '@mui/material';
import { useNavigate, useParams } from 'react-router';
import styled from 'styled-components';
import { useDispatch } from '../../../store';
import { IBookStoreState } from '../../../store/bookV1/types';
import { clearBook, initBook } from '../../../store/bookV1/actions';
import { AlertType, KeyErrors } from '../../../store/enums';
import { Page, PageContent, PageHeader } from '../../../components/UI';
import { distributionInitV1 } from '../../../store/distributionV1/actions';
import { setResultInfo } from '../../../store/resultInformarion/actions';
import { useRoutesHelper } from '../../../helpers/routesHelper';
import { Routes } from '../../../store/routes';
import { Poster } from '../../../components/event';
import { useLazyGetEventByIdQuery } from '../../../core/api/eventsSlice';
import { Ticket } from './ticket';
import { GlobalStateType } from '../../../configureStore';

const dialogTheme = createTheme({
  components: {
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRadius: '15px 16px',
          background: '#1C1C1C',
          width: '410px',
          padding: '13px 20px 16px 20px',
          filter: 'drop-shadow(0px 50px 80px rgba(0, 0, 0, 0.7))',
          [theme.breakpoints.down('sm')]: {
            width: '322px',
            padding: '13px 15px 16px 15px',
          },
        }),
      },
    },
  },
});

export const ReferralPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const event = useSelector<GlobalStateType, IBookStoreState>((state) => state.bookV1);
  const book = useSelector<GlobalStateType, IBookStoreState>((state) => state.bookV1);
  const isRedirection = useSelector<GlobalStateType, boolean>((state) => state.app.isRedirection);
  const isFinalized = useSelector<GlobalStateType, boolean>((state) => state.app.isFinalized);

  const [getEventById, { isLoading }] = useLazyGetEventByIdQuery();

  const { eventSlug, sectorSlug, distributorSlug, token } = useParams<ReferralPageParamsType>();

  const { getResultStatusRoute } = useRoutesHelper();

  useLayoutEffect(() => {
    if (isRedirection || !isFinalized) {
      return;
    }
    dispatch(clearBook());
  }, []);

  useEffect(() => {
    if (distributorSlug) {
      dispatch(distributionInitV1(distributorSlug));
    }
    if (!eventSlug) {
      navigate(Routes.Main);
      return;
    }

    (async () => {
      const { data: event } = await getEventById({ id: eventSlug, distributorSlug: null, token });
      if (!event || !event.slug) {
        dispatch(clearBook());
        dispatch(
          setResultInfo({
            type: AlertType.Error,
            key: KeyErrors.EventNotFound,
            title: 'Alert.SomethingWentWrong',
            message: 'Alert.CheckLinkNoEventFound',
          })
        );
        navigate(getResultStatusRoute(AlertType.Error, KeyErrors.EventNotFound), { replace: true });
        return;
      }
      dispatch(initBook({ event, sectorSlug, seats: book.seats }));
    })();
  }, [isFinalized]);

  useEffect(() => {
    const element = document.getElementById('root');
    element.style.filter = 'blur(2px)';

    return () => {
      element.removeAttribute('style');
    };
  }, []);

  const eventDetails = event?.event;

  return (
    <Page>
      <Header diagonal={true} stretch={false}>
        <Poster src={eventDetails?.posterUrl} alt={eventDetails?.title} title={eventDetails?.title} />
      </Header>
      <ThemeProvider theme={dialogTheme}>
        <Dialog open scroll={'body'}>
          {isLoading && <Skeleton animation="wave" variant="rectangular" height="80vh" />}
          {!isLoading && <Ticket event={event} />}
        </Dialog>
      </ThemeProvider>
    </Page>
  );
};

const Header = styled(PageHeader)`
  justify-content: flex-end;
  max-height: 60vh;
`;

type ReferralPageParamsType = {
  eventSlug: string;
  sectorSlug: string;
  token: string;
  distributorSlug: string;
};
